import { useEffect, useState } from 'react';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateUTC } from '../utils/formatDate';

import { useNavigate, useParams } from 'react-router-dom';
import Button from '../Components/Buttons';
import { RecordRouteApiProvider, useRecordRouteApi } from '../Services/RecordRouteApiProvider';

function RecordRouteHub({ canEdit }) {
  const { getRecordRoute, deleteRecordRoute } = useRecordRouteApi();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recordRoute, setRecordRoute] = useState([]);
  const [totalRecordRoute, setTotalRecordRoute] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: true },
    { name: 'Event Type', selector: (row) => row.route_event_type, sortable: true },
    { name: 'From Type', selector: (row) => row.from_type, sortable: true },
    { name: 'To Type', selector: (row) => row.to_type, sortable: true },
    { name: 'Priority', selector: (row) => row.priority_index, sortable: true },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: true },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true },
  ];

  const fetchRecordRoute = async (skip, limit) => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {
      setLoading(true);
      const response = await getRecordRoute(skip, limit);
      setRecordRoute(response.items);
      setTotalRecordRoute(response.totalCount);
      setItemsPerPage(limit);
    } catch (error) {
      console.error('Error fetching record route:', error);
      setError('Failed to load record route');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecordRoute(0, itemsPerPage);
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/record-route/${row.id}`);
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(`Are you sure you want to delete the selected Record Route?`, async () => {
      try {
        setLoading(true);
        await deleteRecordRoute(selectedId);
        fetchRecordRoute(0, itemsPerPage);
        showAlert('success', 'Record Route was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the record route!');
      }
    });
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Record Route"
                columns={columns}
                data={recordRoute}
                selectableRowsHighlight
                selectableRows
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                paginationTotalRows={totalRecordRoute}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onChangePage={fetchRecordRoute}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/record-route/new-record-route`)
                  }
                >
                  Create New Record Route
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RecordRouteWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <RecordRouteApiProvider>
        <RecordRouteHub canEdit={canEdit} />
      </RecordRouteApiProvider>
    </ConfirmationProvider>
  );
}

export default RecordRouteWrapper;
